import React, { useState } from "react";
import { Modal, ModalHeader, ModalBody, Badge, Nav, NavItem, NavLink, TabContent, TabPane, Button } from "reactstrap";

const EmailPreviewModal = ({ isOpen, toggle, emailLog }) => {
  const [activeTab, setActiveTab] = useState("html");

  if (!emailLog) return null;

  const getStatusBadge = (status) => {
    switch (status) {
      case "Sent":
        return "success";
      case "Failed":
        return "danger";
      case "Pending":
        return "warning";
      default:
        return "primary";
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" centered scrollable>
      <ModalHeader toggle={toggle} className="border-bottom bg-light">
        <div className="d-flex align-items-center gap-2">
          <i className="mdi mdi-email-open-outline font-size-20 text-primary"></i>
          <span className="fw-bold">Email Message Preview</span>
          <Badge color={getStatusBadge(emailLog.status)} className="ms-2 px-3 py-1 font-size-12">
            {emailLog.status}
          </Badge>
        </div>
      </ModalHeader>
      <ModalBody className="p-0" style={{ minHeight: "550px", backgroundColor: "#f4f6f9" }}>
        
        {/* Email Header Details Bar */}
        <div className="p-3 bg-white border-bottom shadow-sm">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-2">
            <h5 className="mb-0 fw-bold text-dark font-size-17">{emailLog.subject}</h5>
            <small className="text-muted font-size-12">
              <i className="mdi mdi-clock-outline me-1"></i>
              {new Date(emailLog.sentAt || emailLog.createdAt).toLocaleString("en-IN")}
            </small>
          </div>

          <div className="row g-2 font-size-13 text-muted">
            <div className="col-md-6">
              <span className="fw-semibold text-dark me-1">To:</span>
              <span className="badge bg-soft-primary text-primary px-2 py-1 font-size-12 me-2">
                {emailLog.recipient}
              </span>
            </div>
            <div className="col-md-6">
              <span className="fw-semibold text-dark me-1">From:</span>
              <span className="text-dark">
                {emailLog.smtpUser || "RS-CRM SMTP Server"}
              </span>
            </div>
            <div className="col-md-6">
              <span className="fw-semibold text-dark me-1">Category:</span>
              <span className="badge bg-soft-info text-info px-2 py-1 font-size-11">
                {emailLog.category || "System"}
              </span>
            </div>
            <div className="col-md-6">
              <span className="fw-semibold text-dark me-1">SMTP Host:</span>
              <span className="text-dark font-monospace font-size-12">
                {emailLog.smtpHost || "smtp.gmail.com"}
              </span>
            </div>
            {emailLog.sentBy && (
              <div className="col-md-12">
                <span className="fw-semibold text-dark me-1">Triggered By:</span>
                <span className="text-primary font-size-12">
                  {emailLog.sentBy.name || "User"} ({emailLog.sentBy.email})
                </span>
              </div>
            )}
            {emailLog.errorMessage && (
              <div className="col-md-12 mt-1">
                <div className="alert alert-danger p-2 mb-0 font-size-12">
                  <i className="mdi mdi-alert-circle me-1"></i>
                  <strong>Delivery Failure Reason:</strong> {emailLog.errorMessage}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Preview View Mode Tabs */}
        <div className="bg-white border-bottom px-3 pt-2">
          <Nav tabs className="nav-tabs-custom border-bottom-0">
            <NavItem>
              <NavLink
                className={`cursor-pointer ${activeTab === "html" ? "active fw-bold" : ""}`}
                onClick={() => setActiveTab("html")}
                style={{ cursor: "pointer" }}
              >
                <i className="mdi mdi-code-tags me-1"></i> HTML Rendered
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={`cursor-pointer ${activeTab === "text" ? "active fw-bold" : ""}`}
                onClick={() => setActiveTab("text")}
                style={{ cursor: "pointer" }}
              >
                <i className="mdi mdi-text-box-outline me-1"></i> Plain Text
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={`cursor-pointer ${activeTab === "headers" ? "active fw-bold" : ""}`}
                onClick={() => setActiveTab("headers")}
                style={{ cursor: "pointer" }}
              >
                <i className="mdi mdi-cog-outline me-1"></i> Raw Metadata
              </NavLink>
            </NavItem>
          </Nav>
        </div>

        {/* Tab Content Display */}
        <div className="p-3">
          <TabContent activeTab={activeTab}>
            {/* HTML Rendered View */}
            <TabPane tabId="html">
              <div className="bg-white p-3 rounded border shadow-sm" style={{ minHeight: "400px" }}>
                <iframe
                  title="Email HTML Preview"
                  srcDoc={emailLog.htmlBody}
                  style={{
                    width: "100%",
                    height: "420px",
                    border: "none",
                    borderRadius: "6px",
                  }}
                />
              </div>
            </TabPane>

            {/* Plain Text View */}
            <TabPane tabId="text">
              <div className="bg-dark text-light p-3 rounded font-monospace font-size-13 border shadow-sm" style={{ minHeight: "350px", whiteSpace: "pre-wrap" }}>
                {emailLog.textBody || emailLog.htmlBody?.replace(/<[^>]+>/g, "") || "No plain text content available."}
              </div>
            </TabPane>

            {/* Headers / Metadata View */}
            <TabPane tabId="headers">
              <div className="bg-light p-3 rounded font-monospace font-size-12 border shadow-sm">
                <div className="mb-2"><strong>Message-ID:</strong> &lt;{emailLog._id}@rscrm.internal&gt;</div>
                <div className="mb-2"><strong>To:</strong> {emailLog.recipient}</div>
                <div className="mb-2"><strong>From:</strong> {emailLog.smtpUser || "RS-CRM Mailer"}</div>
                <div className="mb-2"><strong>Subject:</strong> {emailLog.subject}</div>
                <div className="mb-2"><strong>Date:</strong> {new Date(emailLog.sentAt || emailLog.createdAt).toUTCString()}</div>
                <div className="mb-2"><strong>MIME-Version:</strong> 1.0</div>
                <div className="mb-2"><strong>Content-Type:</strong> text/html; charset=UTF-8</div>
                <div className="mb-2"><strong>X-Mailer:</strong> Nodemailer (RS-CRM Infrastructure)</div>
                <div className="mb-2"><strong>X-SMTP-Host:</strong> {emailLog.smtpHost || "smtp.gmail.com"}</div>
                <div className="mb-2"><strong>Status:</strong> {emailLog.status}</div>
              </div>
            </TabPane>
          </TabContent>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default EmailPreviewModal;
